<template>
  <div>
    <div v-if="loading" class="text-center py-6"><span class="spinner-border text-primary"></span></div>

    <template v-else>
      <div class="d-flex align-items-center gap-3 mb-4">
        <div class="prenatal-gpa rounded px-4 py-2 fw-bolder fs-4">{{ gpaNotation }}</div>
        <div class="text-muted fs-7">G = gestações · P = partos (vaginais + cesáreas) · A = abortos</div>
      </div>

      <div class="row g-3">
        <div class="col-md-3 col-6" v-for="field in countFields" :key="field.key">
          <label class="form-label fs-7">{{ field.label }}</label>
          <input type="number" min="0" max="30" class="form-control form-control-sm" :class="{ 'is-invalid': errors[field.key] }"
                 v-model.number="form[field.key]" :disabled="readonly">
          <small class="text-danger" v-if="errors[field.key]">{{ errors[field.key] }}</small>
        </div>

        <div class="col-md-3 col-6">
          <label class="form-label fs-7">Data do último parto</label>
          <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': errors.last_delivery_date }"
                 v-model="form.last_delivery_date" :disabled="readonly">
          <small class="text-danger" v-if="errors.last_delivery_date">{{ errors.last_delivery_date }}</small>
        </div>

        <div class="col-12">
          <div class="fw-bolder fs-7 mb-2">Complicações em gestações anteriores</div>
          <div class="d-flex flex-wrap gap-5">
            <div class="form-check form-check-sm form-check-custom form-check-solid" v-for="field in complicationFields" :key="field.key">
              <input class="form-check-input" type="checkbox" :id="'obstetric-' + field.key" v-model="form[field.key]" :disabled="readonly">
              <label class="form-check-label fs-7" :for="'obstetric-' + field.key">{{ field.label }}</label>
            </div>
          </div>
        </div>
        <div class="col-12">
          <label class="form-label fs-7">Outras complicações</label>
          <textarea class="form-control form-control-sm" rows="2" v-model="form.other_complications" :disabled="readonly"></textarea>
        </div>
        <div class="col-12">
          <label class="form-label fs-7">Observações</label>
          <textarea class="form-control form-control-sm" rows="2" v-model="form.notes" :disabled="readonly"></textarea>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3" v-if="!readonly">
        <button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="save">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span> Salvar histórico
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import http from '../../../../helpers/http';
import Alert from '../../../../helpers/alert';
import { buildPayload, patientBaseUrl, validationErrors } from '../prenatal';

const COUNT_FIELDS = [
  { key: 'gestations', label: 'Gestações' },
  { key: 'vaginal_births', label: 'Partos vaginais' },
  { key: 'cesarean_births', label: 'Cesáreas' },
  { key: 'abortions', label: 'Abortos' },
  { key: 'ectopic', label: 'Ectópicas' },
  { key: 'stillbirths', label: 'Natimortos' },
  { key: 'living_children', label: 'Filhos vivos' },
];

const COMPLICATION_FIELDS = [
  { key: 'prev_hypertensive', label: 'Síndrome hipertensiva' },
  { key: 'prev_gestational_diabetes', label: 'Diabetes gestacional' },
  { key: 'prev_preterm_birth', label: 'Parto prematuro' },
];

export default {
  name: 'ObstetricHistoryTab',
  emits: ['changed'],
  props: {
    professionalId: { type: [Number, String], required: true },
    patientId: { type: [Number, String], required: true },
    placeId: { type: [Number, String], required: true },
    readonly: Boolean,
  },
  data() {
    return {
      loading: false,
      saving: false,
      form: {},
      errors: {},
      countFields: COUNT_FIELDS,
      complicationFields: COMPLICATION_FIELDS,
    };
  },
  computed: {
    url() {
      return `${patientBaseUrl(this.professionalId, this.patientId)}/obstetric-history`;
    },
    gpaNotation() {
      const births = (Number(this.form.vaginal_births) || 0) + (Number(this.form.cesarean_births) || 0);

      return `G${Number(this.form.gestations) || 0} P${births} A${Number(this.form.abortions) || 0}`;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fill(history) {
      this.form = {
        ...Object.fromEntries(COUNT_FIELDS.map((field) => [field.key, history?.[field.key] ?? 0])),
        ...Object.fromEntries(COMPLICATION_FIELDS.map((field) => [field.key, !!history?.[field.key]])),
        other_complications: history?.other_complications ?? '',
        last_delivery_date: history?.last_delivery_date ?? '',
        notes: history?.notes ?? '',
      };
    },
    async load() {
      this.loading = true;
      try {
        const response = await http.get(this.url, { params: { id_place: this.placeId } });
        this.fill(response.data);
      } catch (_) {
        Alert.toastError('Falha ao carregar o histórico obstétrico.');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.errors = {};

      try {
        const response = await http.put(this.url, { ...buildPayload(this.form), id_place: this.placeId });
        this.fill(response.data);
        Alert.toastSuccess('Histórico obstétrico salvo.');
        this.$emit('changed');
      } catch (error) {
        this.errors = validationErrors(error);
        if (!Object.keys(this.errors).length) {
          Alert.toastError(error.response?.data?.message || 'Falha ao salvar o histórico obstétrico.');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.prenatal-gpa {
  background: #f8f5ff;
  color: #780f5a;
}
</style>
